import React, { useState } from 'react';
import { StyledModal } from '@shoflo/polymer/components/ErrorBoundary/LargeError.styled';
import { getScreenSharingStream } from '@shoflo/polymer/helpers/ScreenShare/ScreenShare';
import { FormGroup } from 'reactstrap';
import { Button } from '@shoflo/polymer/components/Button/Button';
import { useSelector, useDispatch } from 'react-redux';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import useIsMobile from 'hooks/useIsMobile';
import { SCREENSHARE_MODAL } from 'constants/ModalTypes';
import { clearModal, showNotification } from 'actions';
import {
  StyledModalBody,
  StyledCancelButton,
  StyledBodyText,
  ModalHeader,
  StyledErrorHeader,
  StyledErrorTipText,
  StyledErrorWrapper,
  StyledErrorBodyText,
  StyledErrorHeaderText,
  StyledErrorHeaderIcon,
  StyledErrorHeaderIconWrapper
} from './Screenshare.styled';
import cancelScreenShareRequest from 'actions/Broadcast/cancelScreenShareRequest';
import { StudioLogger } from '@studio/logger/StudioLogger';
import { errorReplacer } from '@studio/logger/utils/errorReplacer';
import { useScreenShareStartedAction } from '@studio/metrics/hooks/useScreenShareStartedAction';

const LOGGER = 'app.studio.presenter.ScreenshareModal';
const logger = new StudioLogger(LOGGER);

const ScreenshareModal = () => {
  const dispatch = useDispatch();

  const isOpen = useSelector(({ modal }) => modal?.type === SCREENSHARE_MODAL);

  const [screenShareBlocked, setScreenShareBlocked] = useState(false);

  const {
    meeting: { meetingSession }
  } = useSelector(({ meeting }) => ({
    meeting
  }));

  const { startScreenShareWithTracking } =
    useScreenShareStartedAction(meetingSession);

  const isMobile = useIsMobile();

  const startScreenShare = async () => {
    try {
      // Reset to initial state to ensure the starting state of the modal has no alert
      setScreenShareBlocked(false);

      const { stream, hasAudio, shareType } = await getScreenSharingStream();

      if (stream) {
        await startScreenShareWithTracking(
          {
            screenShareType: shareType,
            tabAudioShared: hasAudio
          },
          async () => {
            await meetingSession.audioVideo.startContentShare(stream);
          }
        );
      }

      dispatch(clearModal(SCREENSHARE_MODAL));
    } catch (error) {
      console.error('Error starting content share:', error);
      logger.error(`An error occurred while sharing screen`, {
        error: errorReplacer(error)
      });
      dispatch(
        showNotification(
          'Unable to Share Screen. Ensure your browser has permission to share screen',
          {
            timeout: 3000,
            type: 'warning'
          }
        )
      );
      if (error?.message?.includes('system')) {
        // NotAllowedError: Permission denied by system.

        setScreenShareBlocked(true);
      }
    }
  };

  const onCancel = () => {
    dispatch(clearModal(SCREENSHARE_MODAL));

    // This will emit socket event to director.
    dispatch(cancelScreenShareRequest());
  };

  return (
    <StyledModal isOpen={isOpen} fade={!isMobile}>
      <ModalHeader>Screen share request approved</ModalHeader>

      <StyledModalBody>
        {screenShareBlocked ? (
          <StyledErrorWrapper>
            <StyledErrorHeader>
              <StyledErrorHeaderIconWrapper>
                <StyledErrorHeaderIcon icon={faInfoCircle} />
              </StyledErrorHeaderIconWrapper>
              <StyledErrorHeaderText>
                Unable to share your screen.
              </StyledErrorHeaderText>
            </StyledErrorHeader>
            <StyledErrorBodyText>
              Your operating system has not given this browser permission to
              share the entire screen. To fix this, you will need to close this
              browser and change your system settings.
            </StyledErrorBodyText>
            <StyledErrorTipText>
              TIP: You are still able to share a tab from this browser.
            </StyledErrorTipText>
          </StyledErrorWrapper>
        ) : (
          <StyledBodyText>
            The director has approved your screen share request! Please click
            the button below to begin sharing.
          </StyledBodyText>
        )}

        <FormGroup>
          <div className="float-end">
            <Button
              color="primary"
              onClick={startScreenShare}
              data-testid="start-screenshare"
            >
              Start screen share
            </Button>
            <StyledCancelButton
              data-testid="cancel-screenshare"
              onClick={onCancel}
            >
              Cancel
            </StyledCancelButton>
          </div>
        </FormGroup>
      </StyledModalBody>
    </StyledModal>
  );
};

export default ScreenshareModal;
